import type { FC } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBackRounded';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

import { apiPaths } from 'src/services/api/apiPaths';
import { routePaths } from 'src/services/routing/routePaths';
import { FilterProvider } from 'src/components/app/context/FilterContext';
import ErrorHandler from 'src/components/common/ErrorHandler';
import Modal from 'src/components/common/Modal';
import PageFrame from 'src/components/common/PageFrame';
import useUserDetail, {
  UserAdminDetailProvider,
} from 'src/components/userAdmin/UserAdminDetailContext';
import InformUserButton from 'src/components/userAdmin/UserDetailPage/components/InformUserButton';
import { confirmationNeeded } from 'src/components/userAdmin/UserDetailPage/helper';

import UserSettings from './components/UserSettings';
import UserSurveySettings from './components/UserSurveySettings';

import { styles } from './index.styles';

export const USER_DETAIL_PAGE_TEST_ID = 'user-detail-page';
export const USER_DETAIL_PAGE_LOADING_TEST_ID = 'user-detail-page-loading';
export const USER_DETAIL_PAGE_REMOVE_TEST_ID = 'user-admin-detail-remove';
export const USER_DETAIL_PAGE_CARD_TEST_ID = 'user-admin-detail-card';
export const USER_DETAIL_PAGE_SAVE_TEST_ID = 'user-detail-page-save';

interface UserDetailPageProps {}

export const UserDetailPage: FC<UserDetailPageProps> = () => {
  const { t } = useTranslation(['common', 'admin']);
  const {
    loading,
    handleSave,
    userModules,
    currentUserModulesPerRole,
    toggleModal,
    userId,
    handleRemoveUser,
    showModal,
  } = useUserDetail();

  const headline =
    userId === 0
      ? t('admin:List.AddNewUserToSurvey')
      : t('admin:List.AddUserToSurvey');

  return (
    <>
      <PageFrame>
        <div
          className="user-admin-detail"
          data-test-id={USER_DETAIL_PAGE_TEST_ID}
        >
          <Box sx={styles.headerColumn}>
            <Box
              sx={styles.backLinkWrapper}
              className="user-admin-detail-back-link-wrapper"
            >
              <Button
                component={Link}
                to={routePaths.USER_ADMIN_LIST}
                startIcon={<ArrowBackIcon />}
                variant="cta"
                color="tertiary"
                data-test-id="back-to-survey-admin-button"
              >
                {t('admin:Detail.BackButton')}
              </Button>
            </Box>
          </Box>
          {loading && (
            <>
              <Box
                sx={styles.loadingIndicator}
                data-test-id={USER_DETAIL_PAGE_LOADING_TEST_ID}
              >
                <CircularProgress />
              </Box>
            </>
          )}

          {!loading && (
            <>
              <Box sx={styles.meta}>
                <Box>
                  <Typography variant="h4">{headline}</Typography>
                </Box>
                <Box
                  sx={{
                    gap: 1,
                    display: 'flex',
                  }}
                >
                  <InformUserButton
                    loading={loading}
                    loadingPosition="start"
                    userId={userId}
                  />
                  {/*remove user button*/}
                  <LoadingButton
                    data-test-id={USER_DETAIL_PAGE_REMOVE_TEST_ID}
                    disabled={!userId}
                    loading={loading}
                    loadingPosition="start"
                    onClick={handleRemoveUser}
                    startIcon={<DeleteOutlineOutlinedIcon />}
                    variant="cta"
                    color="secondary"
                  >
                    {t('admin:Detail.RemoveUser')}
                  </LoadingButton>
                </Box>
              </Box>
              <Stack>
                <Box sx={styles.userSettingsBoxHeader}>
                  <Typography variant={'h5'} sx={{ p: '0.5rem' }}>
                    {t('admin:Detail.generalUserSettings')}
                  </Typography>
                </Box>
                <Divider orientation="horizontal" sx={styles.divider} />
                <Box sx={styles.userSettingsBox}>
                  <UserSettings />
                </Box>
              </Stack>
              <Modal
                headline={t('admin:Detail.userPermissionAdaption.notification')}
                message={t('admin:Detail.userPermissionAdaption.confirmation')}
                isOpen={showModal}
                onClose={toggleModal}
              >
                <DialogActions>
                  <Button fullWidth onClick={() => handleSave()} variant="cta">
                    {t('common:confirm')}
                  </Button>
                  <Button
                    fullWidth
                    onClick={toggleModal}
                    variant="cta"
                    color="secondary"
                  >
                    {t('common:cancel')}
                  </Button>
                </DialogActions>
              </Modal>

              <UserSurveySettings />
            </>
          )}

          <Box sx={styles.saveAndCancelButtons}>
            <Stack direction={'row'}>
              <Button
                component={Link}
                to={routePaths.USER_ADMIN_LIST}
                variant="cta"
                color="secondary"
                sx={{ width: '100%' }}
              >
                {'Cancel'}
              </Button>
              <LoadingButton
                data-test-id={USER_DETAIL_PAGE_SAVE_TEST_ID}
                variant="cta"
                onClick={() =>
                  confirmationNeeded(userModules, currentUserModulesPerRole)
                    ? toggleModal()
                    : handleSave()
                }
                loading={loading}
                fullWidth
              >
                {t('common:save')}
              </LoadingButton>
            </Stack>
          </Box>
          <ErrorHandler
            intercept
            ignoredEndpoints={[apiPaths.ORG_UNIT_CHILDREN]}
          />
        </div>
      </PageFrame>
    </>
  );
};
// Wrap this page in a separate FilterProvider as its call of the useOrgTreeState-hook would run into racing conditions
// with the global initialization of the PlanActionContext and its call of the useOrgTreeState-hook.
// The FilterProvider is initialized for the PermissionCard component.
const UserDetailPageWrapper: FC<UserDetailPageProps> = () => (
  <FilterProvider>
    <UserAdminDetailProvider>
      <UserDetailPage />
    </UserAdminDetailProvider>
  </FilterProvider>
);
export default UserDetailPageWrapper;
